import {useEffect, useState} from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {useRouteMatch} from 'react-router-dom';
import {formatISO} from 'date-fns';
import {findLast, get} from 'lodash';

import api from 'api';
import {
  BlogResponse,
  createAddress,
  ListResponse,
  MMScriptStatus,
  Note,
  RequestDetails,
  ScriptStatus,
  SelectOption,
} from 'types';
import {ScriptStatusOption} from 'components/ScriptStatusSelect';

import {getFullName} from 'helpers/user';
import useCurrentUser from 'hooks/useCurrentUser';
import {ActionMeta, SingleValue} from 'react-select';
import useCustomerInsurances from 'hooks/useCustomerInsurances';
import toast from 'react-hot-toast';
import useSendCustomerNotification from 'hooks/useSendCustomerNotification';
import useRequestSignature from 'hooks/useRequestSignature';
import useUserForm from 'pages/UserForm/useUserForm';
import useUpdateDeliveryAddress from './useUpdateDeliveryAddress';
import {useForm} from 'react-hook-form';
import {TypeGooglePlaceDetail} from 'components/GooglePlaceAutoComplete';

async function getRequestById({
  queryKey,
}: QueryFunctionContext): Promise<RequestDetails> {
  const [, id, order, status] = queryKey;
  const {data} = await api.get<RequestDetails>(`/requests/${id}/`, {
    params: {order, status},
  });
  return data as RequestDetails;
}
async function getMessageTemplates() {
  const {data} = await api.get<ListResponse<BlogResponse>>(`/support/blogs/`, {
    params: {content_type: 'template'},
  });
  return data?.results;
}

async function getRx2GoDetails({queryKey}: QueryFunctionContext): Promise<any> {
  const [, id] = queryKey;
  const {data} = await api.get<any>(`/requests/${id}/rx2go-details/`);
  return data as any;
}

async function updateRequestStatus({
  requestId,
  status,
  drugId,
}: {
  requestId: number;
  status: ScriptStatus | MMScriptStatus;
  drugId?: number;
}) {
  const {data} = await api.post<{status: ScriptStatus}>(
    `/requests/${requestId}/prescriptions/${drugId}/status/`,
    {status}
  );
  return {...data, drugId};
}
async function updatePreferences({
  values,
  requestId,
}: {
  values: PreferenceValues;
  requestId: number;
}) {
  const body = Object.fromEntries(
    Object.entries(values).filter(
      ([_, value]) => value !== undefined && value !== ''
    )
  );
  const {data} = await api.patch(`/requests/${requestId}/`, body);
  return data;
}
function getSerializedErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  } else if (Object.values(error).some((item) => typeof item === 'string')) {
    return Object.values(error)
      .filter((item) => typeof item === 'string')
      .join(', ');
  } else if (Object.values(error).some((item) => Array.isArray(item))) {
    return Object.values(error)
      .filter((item) => Array.isArray(item) && typeof item[0] === 'string')
      .map((item) => item)
      .join(', ');
  }
  return JSON.stringify(error);
}
type FormData = Omit<createAddress, 'label'>;
export type PreferenceValues = {
  preference_date?: string | null;
  preference_time?: string | null;
  is_urgent_delivery?: boolean;
  flag_reason?: string | null;
};

function useRequestDetails() {
  const {params} = useRouteMatch<{id: string}>();
  const currentUser = useCurrentUser();
  const [showInsurance, setShowInsurance] = useState(false);
  const queryClient = useQueryClient();
  const {insurances} = useCustomerInsurances({});
  const {sendNotification} = useSendCustomerNotification();
  const {requestSignature} = useRequestSignature();
  const {addressIsUpdating, updateDeliveryAddress} = useUpdateDeliveryAddress();

  const [status, setStatus] = useState<ScriptStatus | ''>('');
  const [sendChannels, setSendChannels] = useState({
    sms: true,
    email: false,
    push: false,
  });

  const queryCach = queryClient.getQueryCache().getAll();
  const [, , order, statuss] =
    (findLast(queryCach, (q) => q.queryKey[0] === 'requests')
      ?.queryKey as string[]) || [];

  const {data, isLoading, refetch} = useQuery(
    ['request_details', params.id, order, statuss],
    getRequestById,
    {
      refetchInterval:
        status === 'billed' || status === 'ready_for_delivery' ? 3000 : false,
    }
  );
  const {data: rx2goDetails, isLoading: isRx2GoDetailsLoading} = useQuery(
    ['rx2go_details', params.id],
    getRx2GoDetails,
    {
      // refetchOnWindowFocus: false,
      enabled: Boolean(data?.rx2go_id),
    }
  );
  const {data: messageTemplates} = useQuery(
    ['message_templates'],
    getMessageTemplates,
    {
      refetchOnWindowFocus: false,
    }
  );
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [selectCardModal, setSelectCardModal] = useState(false);
  const [accessCardModal, setAccessCardModal] = useState(false);
  const [deliveryAddressId, setDeliveryAddressId] = useState(
    data?.delivery_address?.id
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [location, setLocation] = useState({
    latitude: 40.7596958,
    longitude: -73.9650045,
  });
  const [cardId, setCardIs] = useState(0);
  const [isMessageModalOpen, setMessageModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption>();
  const {createNewAddress, cards} = useUserForm(data?.customer.id);
  const {control, setValue, handleSubmit, reset} = useForm<FormData>();

  useEffect(() => {
    if (cards.length > 0) {
      setCardIs(cards[0].custom_uuid);
    }
  }, [cards]);

  useEffect(() => {
    if (data?.request_origin.startsWith('app')) {
      if (data?.prescriptions.length) {
        setStatus(data.prescriptions[0].status);
      }
    } else {
      if (data?.otcs.length) {
        setStatus(data.otcs[0].status);
      }
    }
  }, [
    setStatus,
    data?.id,
    data?.otcs,
    data?.request_origin,
    data?.prescriptions,
  ]);

  const completeRequest = useMutation(
    'complete_request',
    async () => {
      await api.post(`/requests/${params.id}/complete/`);
      return;
    },
    {
      // onMutate: async () => {
      //   await queryClient.cancelQueries(['request_details', id]);

      //   const previousRequest = queryClient.getQueryData<RequestDetails>([
      //     'request_details',
      //     id,
      //   ]);

      //   if (!previousRequest) throw Error('Request is `undefined`');

      //   const nextRequest: RequestDetails = {
      //     ...previousRequest,
      //     status: 'completed',
      //   };

      //   queryClient.setQueryData(['request_details', id], nextRequest);

      //   return {previousRequest};
      // },
      onSuccess: () => {
        queryClient.invalidateQueries(['request_details']);
      },
    }
  );
  //
  const updateScriptStatus = useMutation(
    'update_script_status',
    updateRequestStatus,
    {
      onError: (err) => {
        const errorMessage = get(
          err,
          'response.data.error_message',
          'Something went wrong'
        );
        setStatus(data?.prescriptions[0].status as ScriptStatus);
        toast.error(errorMessage);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['request_details', params.id]);
      },
    }
  );

  const updatePreference = useMutation('update_preference', updatePreferences, {
    onSettled: () => {
      queryClient.invalidateQueries(['request_details', params.id]);
    },
  });

  const createNote = useMutation(
    'create_note',
    async ({
      text,
      prescriber,
      driver,
    }: {
      text: string;
      prescriber: boolean;
      driver: boolean;
    }): Promise<string> => {
      const {data} = await api.post<{
        text: string;
        is_visible_to_prescribers: boolean;
        is_visible_to_drivers: boolean;
      }>(`/requests/${params.id}/note/`, {
        text,
        is_visible_to_prescribers: prescriber,
        is_visible_to_drivers: driver,
      });
      return data.text;
    },
    {
      onMutate: async ({text, prescriber, driver}) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(['request_details', params.id]);

        // Snapshot the previous value
        const previousRequest = queryClient.getQueryData<RequestDetails>([
          'request_details',
          params.id,
        ]);

        const newNote: Note = {
          id: Date.now(),
          text,
          is_visible_to_prescribers: prescriber,
          is_visible_to_drivers: driver,
          author: getFullName(currentUser),
          avatar: currentUser.avatar,
          created_at: formatISO(Date.now()),
        };

        const nextRequest: RequestDetails | undefined | unknown =
          previousRequest
            ? {
                ...previousRequest,
                notes: [newNote, ...previousRequest.notes],
              }
            : undefined;

        // Optimistically update to the new value
        queryClient.setQueryData(['request_details', params.id], nextRequest);

        // Return a context object with the snapshotted value
        return {previousRequest};
      },

      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, _text, context?: {previousRequest?: RequestDetails}) => {
        queryClient.setQueryData(
          ['request_details', params.id],
          context?.previousRequest
        );
      },

      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries(['request_details', params.id]);
      },
    }
  );

  function onUpdatePreference(values: PreferenceValues) {
    if (!data) return;

    updatePreference.mutateAsync({values, requestId: Number(params.id)});
  }

  async function onChangeScriptStatus(
    newValue: SingleValue<ScriptStatusOption>,
    actionMeta: ActionMeta<SelectOption>
  ) {
    const drugId = Number(actionMeta.name?.split('-')[1]);
    if (newValue) {
      const status = newValue.value;
      await updateScriptStatus.mutateAsync({
        drugId,
        status,
        requestId: Number(params.id),
      });
    }
  }

  function onSubmitNote(text: string, prescriber: boolean, driver: boolean) {
    return createNote.mutate({text, prescriber, driver});
  }

  function complete() {
    completeRequest.mutateAsync();
  }

  function onStatusChange() {
    if (!data) {
      return;
    }
    api
      .patch(`/requests/${data.id}/`, {is_resolved: !data.is_resolved})
      .then(() => {
        refetch();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error_message || 'Something went wrong'
        );
      });
  }

  async function chargeCard(datas: any) {
    const data = await api.post<any>(`/payment/charge/`, datas);
    refetch();
    return data;
  }

  const handleNotifyCustomer = async () => {
    if (!data?.id) {
      return;
    }

    if (data.prescriptions.length > 0) {
      await sendNotification({
        requestId: data?.id,
        request_drugs:
          status === 'completed'
            ? data.prescriptions.filter((item) => item.status !== 'completed')
            : data.prescriptions,
      });
      refetch();
      toast.success('Notification sent successfully');
    }
  };

  const handleRequestSignature = async () => {
    if (!data?.id) {
      return;
    }
    await requestSignature({
      requestId: data?.id,
    });
    toast.success('Notification sent successfully');
  };

  const handleCreation = async (datas: createAddress) => {
    const res = await createNewAddress({
      ...datas,
      customer_id: data?.customer.id,
    });

    if (get(res, 'status') === 200) {
      setOpenAddressModal(false);
      setDeliveryAddressId(res.data.id);
      addressChangeHandler(res.data.id);
      reset();
      setLocation({
        latitude: 40.7596958,
        longitude: -73.9650045,
      });
    } else {
      toast.error('Something wrong with creation. Try again later !');
    }
  };

  const addressChangeHandler = async (id: number | string) => {
    updateDeliveryAddress({
      requestId: data?.id,
      addressId: id,
    })
      .then((res) => {
        setModalOpen(false);
        refetch();
      })
      .catch((er) => {
        const message = get(
          er,
          'response.data.error_message',
          "You can't change address!"
        );
        toast.error(message || "You can't change address!");
      });
  };

  const handleModalClose = () => setModalOpen(false);

  const submitSelectedCard = async () => {
    if (!cardId) {
      return;
    }
    try {
      await chargeCard({
        customer: data?.customer.id,
        payment_method_uuid: cardId,
        copay_items: get(data, 'prescriptions', []),
      });
      setAccessCardModal(false);
      setSelectCardModal(false);
      toast.success('Success');
    } catch (error) {
      const detail = get(error, 'response.data.detail');
      const message = getSerializedErrorMessage(
        detail ? detail : get(error, 'response.data', {})
      );
      toast.error(message);
    }
  };

  const onCharge = async () => {
    if (cards.length < 1) {
      toast.error('Please add billing card to user first');
      return;
    }
    if (cards.length > 1) {
      setSelectCardModal(true);
      return;
    }
    setAccessCardModal(true);
  };

  const onSelectPlace = (placeDetail: TypeGooglePlaceDetail) => {
    setValue('latitude', placeDetail.lat);
    setValue('longitude', placeDetail.lon);
    setTimeout(() => {
      setLocation({latitude: placeDetail.lat, longitude: placeDetail.lon});
    }, 300);
    setValue('zipcode', placeDetail.zipcode);
    setValue('address_line_1', placeDetail.addressLine1);
    setValue('city', placeDetail.city);
    setValue('state', placeDetail.state);
  };

  function modalHandler() {
    setDeliveryAddressId(data?.delivery_address.id);
    setModalOpen(true);
  }

  const sendLeadToMeta = async (type: string) => {
    if (!data?.id) {
      return;
    }
    await api.post(`/requests/${data.id}/send-lead/${type}`);
    refetch();
    toast.success('Lead sent to Meta');
    refetch();
  };

  const sendMessage = async () => {
    if (!data?.id) {
      return;
    }
    if (message.trim() === '') return;
    try {
      await api.post(`/requests/${data.id}/message-customer/`, {
        message:
          message +
          '\n\n\n\n' +
          '----------------------' +
          '\n\n' +
          'Download our app for live updates and refills. medsrx.com/app',
        is_sms: sendChannels.sms,
        is_email: sendChannels.email,
        is_app_push: sendChannels.push,
        // customer_ids: [data?.customer.id],
      });
      toast.success('Message sent successfully');
      setMessageModalOpen(false);
      setMessage('');
      refetch();
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  return {
    data,
    id: params.id,
    showInsurance,
    insurances,
    isLoading,

    markDisabled: Boolean(
      data?.prescriptions.some(
        (drug) =>
          drug.status !== 'ready_for_delivery' &&
          drug.status !== 'cancel_script'
      )
    ),

    setShowInsurance,
    onSubmitNote,
    onChangeScriptStatus,
    complete,
    chargeCard,
    onUpdatePreference,
    refetch,
    onStatusChange,
    handleNotifyCustomer,
    handleRequestSignature,
    handleCreation,
    addressChangeHandler,
    cards,
    addressIsUpdating,
    handleModalClose,
    submitSelectedCard,
    onCharge,
    handleSubmit,
    control,
    setValue,
    onSelectPlace,
    location,
    openAddressModal,
    setOpenAddressModal,
    selectCardModal,
    setSelectCardModal,
    accessCardModal,
    setAccessCardModal,
    modalOpen,
    setModalOpen,
    deliveryAddressId,
    setDeliveryAddressId,
    cardId,
    setCardIs,
    status,
    setStatus,
    modalHandler,
    sendLeadToMeta,
    isMessageModalOpen,
    setMessageModalOpen,
    message,
    setMessage,
    sendMessage,
    messageTemplates: messageTemplates?.map((temp) => ({
      label: temp.title,
      value: temp?.content,
    })),
    selectedTemplate,
    setSelectedTemplate,
    sendChannels,
    setSendChannels,
    currentUser,
    isStatusUpdating: updateScriptStatus.isLoading,
    rx2goDetails,
    isRx2GoDetailsLoading,
  };
}

export default useRequestDetails;
